import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import {
  Shield, RefreshCw, Users, FileText, Building2,
  Receipt, TrendingUp, ToggleLeft, ToggleRight, Edit2,
  Check, X, Download, RotateCcw,
} from 'lucide-react';

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmt = (v, c = 2) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: c }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '---';

const PLANOS = ['basico', 'profissional', 'enterprise'];

const KpiCard = ({ icon: Icon, label, value, sub, color = '#0A3D73' }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3 shadow-sm">
    <div className="p-2 rounded-lg" style={{ background: color + '18' }}>
      <Icon size={18} style={{ color }} strokeWidth={1.5} />
    </div>
    <div>
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</p>
      <p className="text-xl font-black text-slate-800 leading-tight">{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const PlanoTag = ({ plano }) => {
  const map = {
    basico:       'bg-slate-100 text-slate-600 border-slate-300',
    profissional: 'bg-blue-100 text-blue-700 border-blue-300',
    enterprise:   'bg-amber-100 text-amber-700 border-amber-300',
  };
  return (
    <span className={`px-2 py-0.5 text-[9px] font-black uppercase border rounded ${map[plano] || map.basico}`}>
      {plano}
    </span>
  );
};

export default function SuperAdmin() {
  const { user } = useAuth();

  const [metricas, setMetricas]       = useState(null);
  const [tenants, setTenants]         = useState([]);
  const [loadingM, setLoadingM]       = useState(true);
  const [loadingT, setLoadingT]       = useState(true);
  const [editingSlug, setEditingSlug] = useState(null);
  const [editForm, setEditForm]       = useState({ plano: '', observacoes: '' });
  const [saving, setSaving]           = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  const fetchMetricas = useCallback(async () => {
    setLoadingM(true);
    try {
      const { data } = await api.get('/superadmin/metricas');
      setMetricas(data);
    } catch { toast.error('Erro ao carregar métricas'); }
    finally { setLoadingM(false); }
  }, []);

  const fetchTenants = useCallback(async () => {
    setLoadingT(true);
    try {
      const { data } = await api.get('/superadmin/tenants');
      setTenants(data);
    } catch { toast.error('Erro ao carregar tenants'); }
    finally { setLoadingT(false); }
  }, []);

  useEffect(() => {
    fetchMetricas();
    fetchTenants();
  }, [fetchMetricas, fetchTenants]);

  const handleToggleAtivo = async (slug, atualAtivo) => {
    try {
      await api.patch(`/superadmin/tenants/${slug}`, { ativo: !atualAtivo });
      toast.success(`Tenant ${!atualAtivo ? 'ativado' : 'desativado'}`);
      fetchTenants();
    } catch { toast.error('Erro ao alterar status do tenant'); }
  };

  const startEdit = (t) => {
    setEditingSlug(t.slug);
    setEditForm({ plano: t.plano, observacoes: t.observacoes || '' });
  };

  const cancelEdit = () => { setEditingSlug(null); setEditForm({ plano: '', observacoes: '' }); };

  const saveEdit = async (slug) => {
    setSaving(true);
    try {
      await api.patch(`/superadmin/tenants/${slug}`, editForm);
      toast.success('Tenant atualizado');
      setEditingSlug(null);
      fetchTenants();
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleBackup = async () => {
    setActionLoading('backup');
    try {
      const res = await api.get('/admin/backup', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup-repflow-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Backup gerado');
    } catch { toast.error('Erro ao gerar backup'); }
    finally { setActionLoading(''); }
  };

  const handleRecalcular = async () => {
    if (!window.confirm('Recalcular comissões para TODOS os tenants? Esta operação pode demorar.')) return;
    setActionLoading('recalc');
    try {
      await api.post('/admin/recalcular-comissoes');
      toast.success('Comissões recalculadas');
    } catch { toast.error('Erro ao recalcular comissões'); }
    finally { setActionLoading(''); }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Shield size={20} className="text-amber-600" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-lg font-black text-slate-800 uppercase tracking-tight">Painel Superadmin</h1>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
              Gerenciamento global do sistema · {user?.email}
            </p>
          </div>
        </div>
        <button
          onClick={() => { fetchMetricas(); fetchTenants(); }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <RefreshCw size={13} />
          Atualizar
        </button>
      </div>

      {/* KPI Cards */}
      {loadingM ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 h-20 animate-pulse bg-slate-50" />
          ))}
        </div>
      ) : metricas && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard icon={Building2}  label="Tenants ativos"   value={metricas.tenants_ativos}  color="#0A3D73" />
          <KpiCard icon={Users}      label="Usuários ativos"  value={metricas.total_usuarios}  color="#2563EB" />
          <KpiCard icon={FileText}   label="Pedidos totais"   value={fmt(metricas.total_pedidos, 0)} color="#059669" />
          <KpiCard
            icon={TrendingUp}
            label="Faturamento total"
            value={brl(metricas.faturamento_total)}
            sub={`${fmt(metricas.tonelagem_total)} ton faturadas`}
            color="#D97706"
          />
        </div>
      )}

      {/* Tenant Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-xs font-black uppercase tracking-widest text-slate-600 flex items-center gap-2">
            <Building2 size={13} />
            Tenants ({tenants.length})
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                {['Tenant', 'Plano', 'Status', 'Usuários', 'Pedidos', 'Clientes', 'NFs', 'Tonelagem', 'Criado em', 'Ações'].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-[9px] font-black uppercase tracking-wider text-slate-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loadingT ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-slate-400 text-xs">
                    <div className="flex justify-center">
                      <div className="w-6 h-6 border-2 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin" />
                    </div>
                  </td>
                </tr>
              ) : tenants.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-slate-400 text-xs">Nenhum tenant encontrado</td>
                </tr>
              ) : tenants.map(t => (
                <tr key={t.slug} className={`hover:bg-slate-50 transition-colors ${!t.ativo ? 'opacity-50' : ''}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <p className="font-black text-slate-800 font-mono">{t.slug}</p>
                      {t.sem_cadastro && t.slug !== 'repflow' && (
                        <span className="px-1.5 py-0.5 text-[8px] font-black uppercase bg-orange-100 text-orange-600 border border-orange-300 rounded">órfão</span>
                      )}
                      {t.slug === 'repflow' && (
                        <span className="px-1.5 py-0.5 text-[8px] font-black uppercase bg-amber-100 text-amber-600 border border-amber-300 rounded">sistema</span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-400">
                      {t.slug === 'repflow' ? 'RepFlow System' : (t.nome_empresa || t.slug)}
                    </p>
                  </td>
                  <td className="px-3 py-2">
                    {editingSlug === t.slug ? (
                      <select
                        value={editForm.plano}
                        onChange={e => setEditForm(f => ({ ...f, plano: e.target.value }))}
                        className="border border-slate-300 rounded px-1.5 py-1 text-xs font-semibold outline-none focus:border-[#0A3D73]"
                      >
                        {PLANOS.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    ) : (
                      <PlanoTag plano={t.plano} />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 text-[9px] font-black uppercase border rounded ${t.ativo ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-red-100 text-red-600 border-red-300'}`}>
                      {t.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center font-mono font-semibold text-slate-700">{t.total_usuarios}</td>
                  <td className="px-3 py-2 text-center font-mono font-semibold text-slate-700">{t.total_pedidos}</td>
                  <td className="px-3 py-2 text-center font-mono font-semibold text-slate-700">{t.total_clientes}</td>
                  <td className="px-3 py-2 text-center font-mono font-semibold text-slate-700">{t.total_nfs}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold text-slate-700">{fmt(t.tonelagem_total)} t</td>
                  <td className="px-3 py-2 text-slate-500">{fmtDate(t.criado_em)}</td>
                  <td className="px-3 py-2">
                    {editingSlug === t.slug ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => saveEdit(t.slug)}
                          disabled={saving}
                          className="p-1 rounded hover:bg-emerald-100 text-emerald-600 transition-colors disabled:opacity-50"
                          title="Salvar"
                        >
                          <Check size={13} />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="p-1 rounded hover:bg-red-100 text-red-500 transition-colors"
                          title="Cancelar"
                        >
                          <X size={13} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => startEdit(t)}
                          className="p-1 rounded hover:bg-slate-100 text-slate-500 transition-colors"
                          title="Editar plano"
                        >
                          <Edit2 size={13} />
                        </button>
                        {t.slug !== 'repflow' && (
                          <button
                            onClick={() => handleToggleAtivo(t.slug, t.ativo)}
                            className={`p-1 rounded transition-colors ${t.ativo ? 'hover:bg-red-100 text-red-400' : 'hover:bg-emerald-100 text-emerald-500'}`}
                            title={t.ativo ? 'Desativar tenant' : 'Ativar tenant'}
                          >
                            {t.ativo ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Observações inline editor */}
      {editingSlug && (
        <div className="bg-white rounded-xl border border-amber-200 shadow-sm p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-600 mb-2">
            Observações — {editingSlug}
          </p>
          <textarea
            value={editForm.observacoes}
            onChange={e => setEditForm(f => ({ ...f, observacoes: e.target.value }))}
            rows={3}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-[#0A3D73] focus:ring-2 focus:ring-[#0A3D73]/10 transition-all resize-none"
            placeholder="Anotações internas sobre este tenant..."
          />
        </div>
      )}

      {/* Ações Globais */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <h2 className="text-xs font-black uppercase tracking-widest text-slate-600 mb-4">Ações Globais</h2>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleBackup}
            disabled={actionLoading === 'backup'}
            className="flex items-center gap-2 px-4 py-2 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase tracking-widest rounded-lg transition-colors disabled:opacity-60"
          >
            {actionLoading === 'backup' ? (
              <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Download size={13} />
            )}
            Backup Geral
          </button>
          <button
            onClick={handleRecalcular}
            disabled={actionLoading === 'recalc'}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-black uppercase tracking-widest rounded-lg transition-colors disabled:opacity-60"
          >
            {actionLoading === 'recalc' ? (
              <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <RotateCcw size={13} />
            )}
            Recalcular Comissões
          </button>
        </div>
      </div>

    </div>
  );
}
